import { type CSSProperties, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useBranding } from '@/hooks/use-branding';

/**
 * Layout route around every screen that can host a sign-on card, and the owner
 * of the tenant cover.
 *
 * The cover appears with the first card and lasts until the flow ends, so it is
 * never on screen alone: a resumed session that never renders a card stays
 * blank, while a step change — which renders no card in between — keeps this
 * layout mounted and the background steady.
 */
export function SignOnRoutes() {
  const { coverUrl } = useBranding();
  const [hasCard, setHasCard] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const shouldShowCover = hasCard && !hasEnded;
  const cover = shouldShowCover ? coverUrl : null;

  const signOn = useMemo(
    () => ({
      // A card appearing after a completed flow means sign-on restarted (back
      // out of an iframe hand-off), so the cover comes back with it.
      showCard: () => {
        setHasCard(true);
        setHasEnded(false);
      },
      endSignOn: () => setHasEnded(true),
    }),
    []
  );

  return (
    <div
      className='min-h-screen bg-cover bg-center'
      style={
        cover
          ? ({ backgroundImage: `url("${cover}")` } as CSSProperties)
          : undefined
      }
      data-cover={cover ? 'on' : undefined}
    >
      <Outlet context={signOn} />
    </div>
  );
}
